import {DEFAULT_SOUND_ID, getSoundById} from "../common/utils/notification-sounds.registry";

const VOLUME_KEY = "notification_volume";
const SOUND_KEY = "notification_sound";
const DEFAULT_VOLUME = 0.5;

export interface NotificationSettings {
    volume: number;
    soundId: string;
}

type Listener = () => void;
const listeners = new Set<Listener>();

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

const safeRead = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const safeWrite = (key: string, value: string): void => {
    try {
        localStorage.setItem(key, value);
    } catch {
    }
};

const readInitialSettings = (): NotificationSettings => {
    const rawVolume = Number(safeRead(VOLUME_KEY));
    const storedSoundId = safeRead(SOUND_KEY);
    return {
        volume: Number.isFinite(rawVolume) && safeRead(VOLUME_KEY) !== null
            ? clamp01(rawVolume)
            : DEFAULT_VOLUME,

        soundId: getSoundById(storedSoundId ?? DEFAULT_SOUND_ID).id,
    };
};

let settings: NotificationSettings = readInitialSettings();

const emit = () => listeners.forEach((l) => l());

export const getNotificationSettings = (): NotificationSettings => settings;

export const subscribeNotificationSettings = (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const setNotificationVolume = (volume: number): void => {
    settings = {...settings, volume: clamp01(volume)};
    safeWrite(VOLUME_KEY, String(settings.volume));
    emit();
};

export const setNotificationSoundId = (soundId: string): void => {
    const valid = getSoundById(soundId).id;
    settings = {...settings, soundId: valid};
    safeWrite(SOUND_KEY, valid);
    audio = null;
    emit();
};

let audio: HTMLAudioElement | null = null;

const getAudio = (): HTMLAudioElement => {
    if (!audio) {
        audio = new Audio(getSoundById(settings.soundId).src);
        audio.preload = "auto";
    }
    return audio;
};

export const playNotificationSound = (): void => {
    if (settings.volume <= 0) return;
    const el = getAudio();
    el.volume = settings.volume;
    el.currentTime = 0;
    el.play().catch((error) => {
        console.warn("Notification sound blocked or failed:", error);
    });
};

export const previewNotificationSound = (soundId: string): void => {
    const el = new Audio(getSoundById(soundId).src);
    el.volume = settings.volume > 0 ? settings.volume : DEFAULT_VOLUME;
    el.play().catch(() => {
    });
};